import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import {
  axiosDeleteData,
  axiosPatchData,
  axiosPostData,
  axiosPutData,
} from "@shared/api/clients";

import type {
  CreateComplexRequest,
  UpdateComplexRequest,
  UpdateComplexResponse,
  UploadComplexLogoResponse,
} from "./complexes.types";

const COMPLEXES_QUERY_KEY = "complexes";

const getComplexesEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/complexes`;

const ALLOWED_LOGO_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

export const createComplex = async (
  tenantId: string,
  payload: CreateComplexRequest,
): Promise<string> => {
  return axiosPostData<string, CreateComplexRequest>(
    getComplexesEndpoint(tenantId),
    payload,
  );
};

export const useCreateComplexMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (payload: CreateComplexRequest) =>
      createComplex(tenantId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPLEXES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const updateComplex = async (
  tenantId: string,
  complexId: string,
  payload: UpdateComplexRequest,
): Promise<UpdateComplexResponse> => {
  return axiosPutData<UpdateComplexResponse, UpdateComplexRequest>(
    `${getComplexesEndpoint(tenantId)}/${complexId}`,
    payload,
  );
};

export const useUpdateComplexMutation = (
  tenantId: string,
  complexId: string,
) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (payload: UpdateComplexRequest) =>
      updateComplex(tenantId, complexId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPLEXES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const uploadComplexLogo = async (
  tenantId: string,
  complexId: string,
  logo: File,
): Promise<UploadComplexLogoResponse> => {
  const extension = logo.name.split(".").pop()?.toLowerCase();
  if (!extension || !ALLOWED_LOGO_EXTENSIONS.includes(extension)) {
    throw new Error(
      `Extensão de arquivo não permitida. Permitidas: ${ALLOWED_LOGO_EXTENSIONS.join(", ")}`,
    );
  }

  const formData = new FormData();
  formData.append("logo", logo);

  return axiosPatchData<UploadComplexLogoResponse, FormData>(
    `${getComplexesEndpoint(tenantId)}/${complexId}/logo`,
    formData,
  );
};

export const useUploadComplexLogoMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      tenantId,
      complexId,
      logo,
    }: {
      tenantId: string;
      complexId: string;
      logo: File;
    }) => uploadComplexLogo(tenantId, complexId, logo),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          COMPLEXES_QUERY_KEY,
          variables.tenantId,
          variables.complexId,
        ],
      });

      successToast({
        description: translate("common.editSuccess"),
      });
    },

    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};
export const deleteComplex = async (
  tenantId: string,
  complexId: string,
): Promise<void> => {
  await axiosDeleteData<void>(`${getComplexesEndpoint(tenantId)}/${complexId}`);
};

export const useDeleteComplexMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (complexId: string) => deleteComplex(tenantId, complexId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMPLEXES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
